/**
 * useAutoAdvertiseDashboardUrl
 *
 * Zero-config discovery for inter-instance transfers. On the first authenticated
 * admin load, this box POSTs its OWN reachable origin (`window.location.origin`)
 * to the auto-advertise endpoint so a same-owner peer's transfer cockpit/picker
 * can deep-link straight to this box with no manual setup. Without this, the
 * advertised value starts empty and nothing is clickable until a peer is manually
 * configured — this makes the deep-link work out of the box.
 *
 * Fire discipline:
 *   - Fire-and-forget: the server is idempotent (it pushes to the broker only when
 *     the effective value changed) and degrades a loopback/garbage origin to "not
 *     advertised" rather than an error, so the fire is always safe.
 *   - ONCE per page load: a module-level guard means a route change, a live
 *     snapshot tick, or a component remount (incl. React StrictMode's dev
 *     double-mount) never re-fires it. A hard reload resets the guard and retries.
 *   - Admin-only + transfer-feature-gated: the endpoint is requireAdmin, so we
 *     avoid spamming it for non-admins or when transfers are disabled install-wide.
 *
 * Mounted once in the authenticated app shell so it covers every surface that
 * consumes the advertisement (the System Dashboard cockpit and the transfers
 * picker) from a single place.
 */
import { useEffect } from "react";

import { useMe } from "./useMe";
import { isAdminUser } from "../lib/permissions";
import { fireAutoAdvertise } from "./useSetInstanceDashboardUrl";

// Fire only once per full page load. Module-scoped so a route change or a
// component remount cannot re-fire it; a hard reload re-initializes the module.
let didAutoAdvertise = false;

export const useAutoAdvertiseDashboardUrl = (): void => {
  const { user, appConfig } = useMe();
  const eligible =
    isAdminUser(user) && appConfig?.features?.interInstanceTransferEnabled === true;

  useEffect(() => {
    if (!eligible || didAutoAdvertise) return;
    // Claim the one-shot before firing so a re-render or StrictMode double-invoke
    // cannot double-post; a failed fire is not retried until the next page load.
    didAutoAdvertise = true;
    fireAutoAdvertise();
  }, [eligible]);
};
